import { randomLetter, pick, refreshAllRaycasters, disposeLetterEntity } from '../utils.js';
import { LETTER_COLORS } from '../constants.js';

// Module-scope explosion counter. NOT exported — internal to this module.
// Exposed only through the component's behavior (max 3 guard in _onClick).
let activeExplosions = 0;

AFRAME.registerComponent('letter-click', {
  init: function () {
    // Per-instance guard against same-frame double-clicks (e.g. two VR controllers
    // both triggering on the same letter). The global activeExplosions cap does not
    // protect against this: both click events pass the cap check before either
    // increments the counter. Without this flag, the second click calls
    // disposeLetterEntity on an already-removed element, then tries to
    // parentEl.appendChild(replacement) where parentEl is null — throwing a TypeError
    // and permanently losing one letter slot.
    this._isExploding = false;
    this._onClick = this._onClick.bind(this);
    this.el.addEventListener('click', this._onClick);
  },
  _onClick: function () {
    // Per-instance guard must come before the global cap check.
    if (this._isExploding) { return; }
    if (activeExplosions >= 3) {
      console.log('Explosion limit reached (3), ignoring click');
      return;
    }
    this._isExploding = true;

    const letter = this.el.dataset.letter;
    const pos = this.el.getAttribute('position');
    console.log('Letter ' + letter + ' clicked at position (' +
      pos.x.toFixed(2) + ', ' + pos.y.toFixed(2) + ', ' + pos.z.toFixed(2) + ')');

    // Remove from raycaster BEFORE explosion — classList.remove + refreshAllRaycasters
    // is the only correct approach; visible:false does NOT remove entities from raycaster.
    this.el.classList.remove('clickable-letter');
    refreshAllRaycasters(this.el.sceneEl);

    // Capture parent reference NOW, before disposeLetterEntity removes el from DOM.
    // disposeLetterEntity calls el.parentNode.removeChild(el), which nulls el.parentNode.
    const parentEl = this.el.parentNode;
    const sceneEl = this.el.sceneEl;

    // Increment BEFORE creating explosion entity so the counter is accurate
    // even if an exception is thrown synchronously during entity creation.
    activeExplosions++;

    const explosion = document.createElement('a-entity');
    explosion.setAttribute('position', pos);

    // Attach explosion-complete listener BEFORE appending to scene.
    // Third argument to A-Frame emit() is 'bubbles'. We pass false to prevent
    // the event propagating to parent entities (sceneEl etc.).
    explosion.addEventListener('explosion-complete', () => {
      activeExplosions--;
      console.log('Explosion complete, spawning replacement letter');

      const newLetter = randomLetter(letter);
      const newColor = pick(LETTER_COLORS);

      const entity = document.createElement('a-entity');
      entity.setAttribute('position', pos);
      entity.setAttribute('text', {
        value: newLetter,
        color: newColor,
        align: 'center',
        width: 8,
        font: 'mozillavr',
        shader: 'sdf',
      });
      entity.dataset.letter = newLetter;
      entity.dataset.color = newColor;
      entity.setAttribute('billboard', '');
      entity.setAttribute('letter-hover', '');
      entity.setAttribute('scale', '0.001 0.001 0.001');

      // Attach animationcomplete listener BEFORE setting the animation attribute.
      // Animation starts on appendChild (connectedCallback), not on setAttribute.
      // The listener-before-attribute order is defensive: it prevents a silent
      // permanent un-clickable state if the order is ever accidentally reversed.
      //
      // 'animationcomplete__scale' (scoped) fires only for this specific animation.
      // 'animationcomplete' (unscoped) fires for ANY animation on the entity —
      // if a second animation is ever added, the unscoped event fires first,
      // making the letter clickable while still invisible.
      entity.addEventListener('animationcomplete__scale', function () {
        entity.classList.add('clickable-letter');
        entity.setAttribute('letter-click', '');
        if (entity.sceneEl) {
          refreshAllRaycasters(entity.sceneEl);
        }
      }, { once: true });

      // Set animation AFTER listener is attached.
      // Explicit `from` prevents A-Frame from reading a potentially stale scale
      // attribute as the starting value (possible if component data hasn't flushed yet).
      entity.setAttribute('animation__scale', {
        property: 'scale',
        from: '0.001 0.001 0.001',
        to: '1 1 1',
        dur: 400,
        easing: 'easeOutElastic',
      });

      parentEl.appendChild(entity);
    }, { once: true });

    // disposeLetterEntity is called INSIDE the try block, AFTER the explosion entity
    // is successfully added to the scene.
    //
    // Ordering rationale: if we disposed the letter BEFORE appending the explosion,
    // any failure in sceneEl.appendChild or particle-explosion.init would leave the
    // clicked letter permanently gone from the DOM with no replacement — the letter
    // slot is silently lost forever. By disposing AFTER a successful append, failure
    // in particle-explosion.init is still possible (the entity is in the DOM but
    // component init threw), but in that case we recover the original letter in catch.
    try {
      sceneEl.appendChild(explosion);
      explosion.setAttribute('particle-explosion', '');
      // Only dispose the old letter once we know the explosion entity is live.
      disposeLetterEntity(this.el);
    } catch (err) {
      console.error('Failed to create explosion entity:', err);
      activeExplosions--;
      this._isExploding = false;
      // Restore the original letter to clickable state so the user can retry.
      this.el.classList.add('clickable-letter');
      refreshAllRaycasters(sceneEl);
    }
  },
  remove: function () {
    this.el.removeEventListener('click', this._onClick);
  },
});

if (import.meta.hot) { import.meta.hot.decline(); }
